import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Paper, Typography
} from '@mui/material';
import { 
    Close as CloseIcon, Delete as DeleteIcon, ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon 
} from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

function buildDefaultEnumData(langs) {
    const emptyLabels = Object.fromEntries((langs || []).map(lang => [lang, '']));
    return {
        '001': { ...emptyLabels },
        '002': { ...emptyLabels },
        '003': { ...emptyLabels }
    };
}

export default function EnumDialog({ langs, readOnly, initialData, onSubmit, onClose }) {
    const { t } = useTranslation();
    const [data, setData] = useState(() => {
        const hasInitial = initialData && Object.keys(initialData).length > 0;
        return hasInitial ? initialData : buildDefaultEnumData(langs);
    });

    const handleAddRow = () => {
        const currentData = data || {};
        
        // Trouve le prochain numéro disponible (001, 002, 003, ...)
        let counter = 1;
        let newKey;
        do {
            newKey = String(counter).padStart(3, '0');
            counter++;
        } while (currentData[newKey]);

        const newData = {
            ...currentData,
            [newKey]: {
                ...Object.fromEntries(langs.map(lang => [lang, '']))
            }
        };
        setData(newData);
    };

    const handleRemoveRow = (key) => {
        const newData = { ...data };
        delete newData[key];
        setData(newData);
    };

    const handleChange = (key, lang, value) => {
        const newData = {
            ...data,
            [key]: {
                ...data[key],
                [lang]: value
            }
        };
        setData(newData);
    };

    const handleKeyChange = (oldKey, newKey) => {
        // Préserve l'ordre des clés en recréant un nouvel objet dans le bon ordre
        const entries = Object.entries(data);
        const newEntries = entries.map(([k, v]) => k === oldKey ? [newKey, v] : [k, v]);
        const newData = Object.fromEntries(newEntries);
        setData(newData);
    };

    const handleMoveRow = (currentKey, direction) => {
        const entries = Object.entries(data);
        const currentIndex = entries.findIndex(([key]) => key === currentKey);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        if (newIndex < 0 || newIndex >= entries.length) return;

        const newEntries = [...entries];
        [newEntries[currentIndex], newEntries[newIndex]] = [newEntries[newIndex], newEntries[currentIndex]];
        setData(Object.fromEntries(newEntries));
    };

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {readOnly ? t('View field values') : t('Configure field values')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ pt: 0, minHeight: '300px' }}>
                <TableContainer 
                    component={Paper} 
                    sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        boxShadow: 1
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ 
                                    p: 1,
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'medium'
                                }}>
                                    {t('Internal value')}
                                </TableCell>
                                {langs.map((lang) => (
                                    <TableCell key={lang} sx={{ 
                                        backgroundColor: 'background.paper',
                                        fontWeight: 'medium'
                                    }}>
                                        {t('Label') + ' (' + t(`lang.${lang}`) + ')'}
                                    </TableCell>
                                ))}
                                {!readOnly && (
                                    <TableCell sx={{ 
                                        backgroundColor: 'background.paper',
                                        fontWeight: 'medium',
                                        width: '140px'
                                    }}>
                                        {t('Actions')}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data && Object.entries(data).map(([key, row]) => (
                                <TableRow 
                                    key={key}
                                    hover
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '& td': { py: 1 }
                                    }}
                                >
                                    <TableCell>
                                        {readOnly ? (
                                            <Typography>{key}</Typography>
                                        ) : (
                                            <TextField
                                                size="small"
                                                value={key}
                                                onChange={(e) => handleKeyChange(key, e.target.value)}
                                                variant="outlined"
                                                sx={{ minWidth: '80px' }}
                                            />
                                        )}
                                    </TableCell>
                                    {langs.map((lang) => (
                                        <TableCell key={lang}>
                                            {readOnly ? (
                                                <Typography>{row[lang]}</Typography>
                                            ) : (
                                                <TextField
                                                    size="small"
                                                    value={row[lang]}
                                                    onChange={(e) => handleChange(key, lang, e.target.value)}
                                                    variant="outlined"
                                                    fullWidth
                                                />
                                            )}
                                        </TableCell>
                                    ))}
                                    {!readOnly && (
                                        <TableCell>
                                            <IconButton 
                                                color="primary"
                                                onClick={() => handleMoveRow(key, 'up')}
                                                disabled={Object.keys(data)[0] === key}
                                            >
                                                <ArrowUpwardIcon />
                                            </IconButton>
                                            <IconButton 
                                                color="primary"
                                                onClick={() => handleMoveRow(key, 'down')}
                                                disabled={Object.keys(data)[Object.keys(data).length - 1] === key}
                                            >
                                                <ArrowDownwardIcon />
                                            </IconButton>
                                            <IconButton 
                                                color="primary"
                                                onClick={() => handleRemoveRow(key)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {!readOnly && (
                    <Button 
                        onClick={handleAddRow}
                        variant="contained"
                        color="primary"
                        sx={{ 
                            mt: 2,
                            textTransform: 'none'
                        }}
                        startIcon={<AddCircleOutlineIcon />}
                    >
                        {t('Add row')}
                    </Button>
                )}
            </DialogContent>
            {readOnly ? (
                <DialogActions>
                    <Button onClick={onClose}>{t('Close')}</Button>
                </DialogActions>
            ) : (
                <DialogActions>
                    <Button onClick={onClose}>{t('Cancel')}</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onSubmit(data)}
                    >
                        {t('Validate')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}